import React, { useEffect, useState, useRef } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CopyIcon as Copy, PlusIcon as Plus, ArrowsClockwiseIcon as ArrowsClockwise, CheckCircleIcon as CheckCircle, GraduationCapIcon as GraduationCap, BoneIcon as Bone, TShirtIcon as TShirt, TrashIcon as Trash, CameraIcon as Camera, PencilSimpleIcon as PencilSimple, ShareNetworkIcon as ShareNetwork, ChatCircleIcon as ChatCircle, EnvelopeSimpleIcon as EnvelopeSimple, QrCodeIcon as QrCode, PaintBrushIcon as PaintBrush, HourglassIcon as Hourglass, XCircleIcon as XCircle } from "@phosphor-icons/react";
import { thumbUrl } from "@/lib/utils-app";
import { QRCodeSVG } from "qrcode.react";

/**
 * `/admin/armstrong` — single management console for the Armstrong
 * storefront. Three tabs:
 *   1. Training subscriptions — admin enters client name/email/amount;
 *      backend generates a private Stripe payment link the admin
 *      copies + texts to the client.
 *   2. Dog food — CRUD product catalog + view food orders.
 *   3. Merch (Printify) — view synced products and merch orders.
 */
export default function AdminArmstrong() {
  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-16">
        <div className="mb-8">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-2">
            Admin · Armstrong storefront
          </p>
          <h1 className="font-spectral text-3xl sm:text-4xl text-ink">Manage the Armstrong page</h1>
          <p className="text-[#6B6B6B] mt-2 text-sm">
            Public storefront: <Link to="/armstrong" className="underline decoration-gold underline-offset-4 hover:text-ink">/armstrong</Link>
          </p>
        </div>

        <NotificationSettingsCard />

        <Tabs defaultValue="training">
          <TabsList className="mb-6">
            <TabsTrigger value="training" data-testid="tab-training">
              <GraduationCap size={16} className="mr-1.5" /> Training
            </TabsTrigger>
            <TabsTrigger value="food" data-testid="tab-food">
              <Bone size={16} className="mr-1.5" /> Dog Food
            </TabsTrigger>
            <TabsTrigger value="merch" data-testid="tab-merch">
              <TShirt size={16} className="mr-1.5" /> Merch
            </TabsTrigger>
            <TabsTrigger value="custom" data-testid="tab-custom">
              <PaintBrush size={16} className="mr-1.5" /> Custom orders
            </TabsTrigger>
          </TabsList>

          <TabsContent value="training"><TrainingTab /></TabsContent>
          <TabsContent value="food"><FoodTab /></TabsContent>
          <TabsContent value="merch"><MerchTab /></TabsContent>
          <TabsContent value="custom"><CustomOrdersTab /></TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

// ============================================================== Notifications
function NotificationSettingsCard() {
  const [email, setEmail] = useState("");
  const [saved, setSaved] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/armstrong/admin/settings").then((r) => {
      const v = r.data?.notification_email || "";
      setEmail(v);
      setSaved(v);
    }).catch(() => {});
  }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const { data } = await api.patch("/armstrong/admin/settings", { notification_email: email });
      setSaved(data.notification_email || "");
      setEmail(data.notification_email || "");
      setEditing(false);
      toast.success(saved ? "Email updated" : "Email saved — you'll be notified on every paid order");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!editing && saved) {
    return (
      <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="notify-card-set">
        <p className="text-sm text-emerald-900">
          🔔 Order notifications go to <strong>{saved}</strong>
        </p>
        <Button size="sm" variant="ghost" onClick={() => setEditing(true)} className="text-[12px] h-8" data-testid="notify-change-btn">
          Change
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={save}
      className="mb-6 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-4 flex items-center gap-3 flex-wrap"
      data-testid="notify-card-edit"
    >
      <div className="flex-1 min-w-[260px]">
        <p className="text-sm font-medium text-amber-900 mb-1">
          🔔 Where should we email you when a food or merch order is placed?
        </p>
        <p className="text-[12px] text-amber-700/80 mb-2">
          You&rsquo;ll get an email the moment Stripe confirms payment — every food and merch order. Leave blank to disable.
        </p>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="orders@armstrongk9registry.com"
          className="bg-white max-w-sm"
          data-testid="notify-email-input"
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={busy} className="rounded-full bg-ink hover:bg-crimson text-white h-9 text-[13px]" data-testid="notify-save-btn">
          {busy ? "Saving…" : "Save"}
        </Button>
        {saved && (
          <Button type="button" variant="ghost" onClick={() => { setEditing(false); setEmail(saved); }} className="h-9 text-[13px]">
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

// ============================================================== Training
function TrainingTab() {
  const [list, setList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const reload = () => api.get("/armstrong/admin/training/subscriptions").then((r) => setList(r.data.subscriptions || []));

  useEffect(() => { reload(); }, []);

  const cancel = async (id) => {
    if (!window.confirm("Cancel this training subscription? The client will keep access through the end of their paid period, then auto-renewal stops. No prorated refund is issued.")) return;
    try {
      await api.post(`/armstrong/admin/training/subscriptions/${id}/cancel`);
      toast.success("Cancellation scheduled — service ends at period close");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    }
  };

  const pause = async (id) => {
    if (!window.confirm("Pause billing for this client? Stripe stops invoicing until you resume.")) return;
    try {
      await api.post(`/armstrong/admin/training/subscriptions/${id}/pause`);
      toast.success("Paused");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Pause failed");
    }
  };

  const resume = async (id) => {
    try {
      await api.post(`/armstrong/admin/training/subscriptions/${id}/resume`);
      toast.success("Resumed");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resume failed");
    }
  };

  return (
    <div className="space-y-6">
      {/* Heads-up: training services are no longer sold via this
          storefront once migration runs. Existing subscriptions stay
          manageable for cleanup; the public page replaces this tab
          with a phone/email contact card. */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="admin-armstrong-training-notice">
        <span className="text-amber-700 mt-0.5">⚠</span>
        <div className="text-[12.5px] text-amber-900 leading-relaxed">
          <strong>Training is no longer a storefront product.</strong>{" "}
          Existing subscriptions remain manageable below so you can wind
          them down cleanly. New clients should be onboarded directly via
          phone (510-565-9257) or email (armstrongsupremeteamk9@gmail.com).
          After running the Armstrong → seller migration, the public
          training section is replaced with a contact card.{" "}
          <Link to="/admin/armstrong/migrate" className="underline font-semibold">
            Open migration tool →
          </Link>
        </div>
      </div>
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-spectral text-2xl">Training subscriptions</h2>
          <p className="text-sm text-[#6B6B6B] mt-1">Generate a private Stripe payment link per client. Copy & text it to them.</p>
        </div>
        <Button className="rounded-full bg-ink hover:bg-crimson text-white" onClick={() => setDialogOpen(true)} data-testid="training-create-btn">
          <Plus size={16} className="mr-1" /> New training invite
        </Button>
      </div>

      <div className="rounded-2xl border border-[#E8E0CC] bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="training-table">
          <thead className="bg-[#FAF3DA] text-left">
            <tr>
              <th className="py-2 px-4 font-medium">Client</th>
              <th className="py-2 px-4 font-medium">Plan</th>
              <th className="py-2 px-4 font-medium">Deposit</th>
              <th className="py-2 px-4 font-medium">$/mo</th>
              <th className="py-2 px-4 font-medium">Status</th>
              <th className="py-2 px-4 font-medium">Payment link</th>
              <th className="py-2 px-4 font-medium"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E8E0CC]">
            {list.length === 0 && (
              <tr><td colSpan={7} className="py-6 text-center text-[#6B6B6B]">No training subscriptions yet.</td></tr>
            )}
            {list.map((s) => (
              <tr key={s.id} data-testid={`training-row-${s.id}`}>
                <td className="py-2.5 px-4">
                  <div className="font-medium">{s.client_name}</div>
                  <div className="text-[12px] text-[#6B6B6B]">{s.client_email}</div>
                </td>
                <td className="py-2.5 px-4">{s.plan_label}</td>
                <td className="py-2.5 px-4 font-tabular text-[#3a3a3a]">
                  {s.setup_fee_usd ? `$${Number(s.setup_fee_usd).toFixed(2)}` : "—"}
                </td>
                <td className="py-2.5 px-4 font-tabular">${s.monthly_amount_usd}</td>
                <td className="py-2.5 px-4">
                  <StatusBadge status={s.status} />
                </td>
                <td className="py-2.5 px-4">
                  {s.stripe_payment_link_url ? (
                    <ShareRowButton invite={s} />
                  ) : "—"}
                </td>
                <td className="py-2.5 px-4 text-right">
                  {s.status === "paused" ? (
                    <Button size="sm" variant="outline" className="rounded-full h-7 text-[11px]" onClick={() => resume(s.id)} data-testid={`training-resume-${s.id}`}>
                      Resume
                    </Button>
                  ) : (s.status === "active") ? (
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => pause(s.id)} data-testid={`training-pause-${s.id}`}>
                        Pause
                      </Button>
                      <Button size="sm" variant="ghost" className="text-crimson hover:bg-crimson/10 h-7 text-[11px]" onClick={() => cancel(s.id)} data-testid={`training-cancel-${s.id}`}>
                        Cancel
                      </Button>
                    </div>
                  ) : (s.status === "pending" || s.status === "cancelling") ? (
                    <Button size="sm" variant="ghost" className="text-crimson hover:bg-crimson/10 h-7 text-[11px]" onClick={() => cancel(s.id)} data-testid={`training-cancel-${s.id}`}>
                      Cancel
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TrainingInviteDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onCreated={() => { setDialogOpen(false); reload(); }} />
    </div>
  );
}

// ============================================================== ShareLinkPanel
/**
 * Multi-channel share panel rendered once a training payment link is
 * created (or via the per-row "Share" button). Auto-generated QR code,
 * one-tap SMS + email + native Web Share, plus the bare copy-link
 * fallback. Designed for the husband to text/email/AirDrop the link
 * to a new client in ~5 seconds.
 */
function ShareLinkPanel({ url, clientName, clientEmail, monthlyAmount, setupFee, onDone }) {
  const firstName = (clientName || "there").split(" ")[0];
  const priceLine = setupFee
    ? `$${(setupFee + monthlyAmount).toFixed(2)} today (deposit + first month), then $${monthlyAmount.toFixed(2)}/mo`
    : `$${monthlyAmount.toFixed(2)}/mo`;
  const smsBody = `Hi ${firstName}, here's your Armstrong K9 dog training payment link: ${url} (${priceLine}). Cancel any time, no prorated refunds.`;
  const emailSubject = "Your Armstrong K9 dog training payment link";
  const emailBody = `Hi ${firstName},\n\nThanks for choosing Armstrong K9 for your dog's training. Here's your secure Stripe payment link:\n\n${url}\n\nWhat happens after you pay:\n  • ${priceLine}\n  • Sessions begin within 7 days\n  • You can cancel auto-renewal any time from your Stripe portal (link in your receipt email)\n  • We do not issue prorated refunds — service continues through the paid period\n\nQuestions? Just reply to this email.\n\n— The Armstrong family`;

  const copy = () => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  const sendSms = () => {
    // iOS expects `sms:&body=…`; Android works with both. Try both.
    const href = `sms:?&body=${encodeURIComponent(smsBody)}`;
    window.location.href = href;
  };

  const sendEmail = () => {
    const href = `mailto:${encodeURIComponent(clientEmail || "")}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    window.location.href = href;
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      copy();
      return;
    }
    try {
      await navigator.share({
        title: "Armstrong K9 dog training",
        text: `Hi ${firstName}, here's your dog training payment link — ${priceLine}`,
        url,
      });
    } catch (_e) {
      /* user cancelled — no-op */
    }
  };

  return (
    <div className="space-y-4" data-testid="share-link-panel">
      <p className="text-sm text-[#3a3a3a]">
        Send this link to <strong>{clientName}</strong>. Stripe charges the deposit + first month on payment, then auto-renews monthly.
      </p>

      {/* QR Code */}
      <div className="flex items-center gap-4 rounded-xl border border-[#E8E0CC] bg-cream p-4">
        <div className="bg-white rounded-lg p-2 border border-[#E8E0CC] flex-shrink-0">
          <QRCodeSVG value={url} size={96} bgColor="#FFFFFF" fgColor="#0E0E0E" level="M" />
        </div>
        <div className="text-[12px] text-[#3a3a3a] leading-relaxed">
          <strong className="text-ink">Scan with phone camera.</strong><br />
          Hand your phone to the client or print this on a flier — they can pay without you typing a single character.
        </div>
      </div>

      {/* Channel buttons */}
      <div className="grid grid-cols-2 gap-2">
        <Button onClick={sendSms} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="share-sms-btn">
          <ChatCircle size={14} weight="duotone" className="mr-1.5" /> Text it
        </Button>
        <Button onClick={sendEmail} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="share-email-btn">
          <EnvelopeSimple size={14} weight="duotone" className="mr-1.5" /> Email it
        </Button>
        <Button onClick={nativeShare} variant="outline" className="rounded-full border-ink" data-testid="share-native-btn">
          <ShareNetwork size={14} weight="duotone" className="mr-1.5" /> Share via…
        </Button>
        <Button onClick={copy} variant="outline" className="rounded-full" data-testid="share-copy-btn">
          <Copy size={14} weight="duotone" className="mr-1.5" /> Copy link
        </Button>
      </div>

      <details className="rounded-lg border border-[#E8E0CC] bg-white px-3 py-2">
        <summary className="cursor-pointer text-[11px] text-[#6B6B6B] hover:text-ink select-none">
          Show raw URL & message preview
        </summary>
        <div className="mt-2 break-all text-[11px] font-mono text-[#3a3a3a] bg-cream p-2 rounded">{url}</div>
        <div className="mt-2 p-2 rounded bg-cream border border-[#E8E0CC]">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B] mb-1">SMS preview</p>
          <p className="text-[12px] text-[#3a3a3a] leading-relaxed whitespace-pre-line">{smsBody}</p>
        </div>
      </details>

      <Button variant="outline" className="w-full rounded-full" onClick={onDone}>Done</Button>
    </div>
  );
}

// ============================================================ ShareRowButton
function ShareRowButton({ invite }) {
  const [open, setOpen] = useState(false);
  if (!invite.stripe_payment_link_url) return null;
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="rounded-full text-[11px] h-7"
        onClick={() => setOpen(true)}
        data-testid={`training-share-${invite.id}`}
      >
        <ShareNetwork size={11} className="mr-1" /> Share
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="share-link-dialog">
          <DialogHeader>
            <DialogTitle>Share payment link · {invite.client_name}</DialogTitle>
          </DialogHeader>
          <ShareLinkPanel
            url={invite.stripe_payment_link_url}
            clientName={invite.client_name}
            clientEmail={invite.client_email}
            monthlyAmount={invite.monthly_amount_usd}
            setupFee={invite.setup_fee_usd || 0}
            onDone={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

function TrainingInviteDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ client_name: "", client_email: "", monthly_amount_usd: 150, setup_fee_usd: 150, plan_label: "Dog Training", notes: "" });
  const [busy, setBusy] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        monthly_amount_usd: parseFloat(form.monthly_amount_usd),
        setup_fee_usd: form.setup_fee_usd ? parseFloat(form.setup_fee_usd) : null,
      };
      const { data } = await api.post("/armstrong/admin/training/invite", payload);
      setCreatedLink(data.stripe_payment_link_url);
      toast.success("Payment link created — copy and send to your client");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setCreatedLink(null);
    setForm({ client_name: "", client_email: "", monthly_amount_usd: 150, setup_fee_usd: 150, plan_label: "Dog Training", notes: "" });
    if (createdLink) onCreated(); else onClose();
  };

  const monthly = parseFloat(form.monthly_amount_usd) || 0;
  const setup = parseFloat(form.setup_fee_usd) || 0;
  const firstCharge = monthly + setup;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md" data-testid="training-invite-dialog">
        <DialogHeader>
          <DialogTitle>{createdLink ? "Payment link ready" : "New training invite"}</DialogTitle>
        </DialogHeader>
        {createdLink ? (
          <ShareLinkPanel
            url={createdLink}
            clientName={form.client_name}
            clientEmail={form.client_email}
            monthlyAmount={monthly}
            setupFee={setup}
            onDone={close}
          />
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Client name</Label>
              <Input required value={form.client_name} onChange={(e) => setForm({...form, client_name: e.target.value})} data-testid="invite-name" />
            </div>
            <div>
              <Label className="text-xs">Client email</Label>
              <Input required type="email" value={form.client_email} onChange={(e) => setForm({...form, client_email: e.target.value})} data-testid="invite-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs flex items-center gap-1">
                  Setup deposit <span className="text-[10px] text-[#9A9A9A]">(one-time)</span>
                </Label>
                <Input type="number" min="0" step="0.01" value={form.setup_fee_usd} onChange={(e) => setForm({...form, setup_fee_usd: e.target.value})} placeholder="150" data-testid="invite-setup-fee" />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1">
                  Monthly rate <span className="text-[10px] text-[#9A9A9A]">(recurring)</span>
                </Label>
                <Input required type="number" min="1" step="0.01" value={form.monthly_amount_usd} onChange={(e) => setForm({...form, monthly_amount_usd: e.target.value})} data-testid="invite-amount" />
              </div>
            </div>
            <p className="text-[11px] text-[#6B6B6B] -mt-1">
              Leave deposit blank for $0 setup. Both line items appear on Stripe checkout — deposit is charged once, monthly auto-renews.
            </p>
            <div className="rounded-lg bg-cream border border-[#E8E0CC] p-3 text-[12px] text-[#3a3a3a]" data-testid="invite-summary">
              <div className="flex justify-between"><span>First charge:</span><strong className="font-tabular">${firstCharge.toFixed(2)}</strong></div>
              {setup > 0 && <div className="text-[11px] text-[#6B6B6B] mt-0.5">${setup.toFixed(2)} deposit + ${monthly.toFixed(2)} first month</div>}
              <div className="flex justify-between mt-1 pt-1 border-t border-[#E8E0CC]"><span>Monthly thereafter:</span><strong className="font-tabular">${monthly.toFixed(2)}/mo</strong></div>
            </div>
            <div>
              <Label className="text-xs">Plan label</Label>
              <Input value={form.plan_label} onChange={(e) => setForm({...form, plan_label: e.target.value})} />
            </div>
            <div>
              <Label className="text-xs">Internal notes (private)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy} className="rounded-full bg-gold-gradient text-ink font-semibold w-full" data-testid="invite-submit">
                {busy ? "Generating…" : "Generate payment link"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================== Food
function FoodTab() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    const [p, o] = await Promise.all([
      api.get("/armstrong/admin/food"),
      api.get("/armstrong/admin/orders?kind=food"),
    ]);
    setProducts(p.data.products || []);
    setOrders(o.data.orders || []);
  };

  useEffect(() => { reload(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/armstrong/admin/food/${id}`);
    toast.success("Deleted");
    reload();
  };

  const markDelivered = async (id) => {
    await api.post(`/armstrong/admin/orders/${id}/mark-delivered`);
    toast.success("Marked delivered");
    reload();
  };

  return (
    <div className="space-y-8">
      <DeliveryZonePanel />
      <section>
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-spectral text-2xl">Food catalog</h2>
          <Button className="rounded-full bg-ink hover:bg-crimson text-white" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="food-create-btn">
            <Plus size={16} className="mr-1" /> New product
          </Button>
        </div>
        <p className="text-[12px] text-[#6B6B6B] mb-4">
          Tap the price or photo on any card to edit it directly — no dialog needed.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.length === 0 && <p className="text-sm text-[#6B6B6B]">No products yet.</p>}
          {products.map((p) => (
            <FoodProductCard
              key={p.id}
              product={p}
              onChanged={reload}
              onEdit={() => { setEditing(p); setDialogOpen(true); }}
              onDelete={() => remove(p.id)}
            />
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-spectral text-2xl mb-4">Food orders</h2>
        <div className="rounded-2xl border border-[#E8E0CC] bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF3DA] text-left">
              <tr>
                <th className="py-2 px-4 font-medium">When</th>
                <th className="py-2 px-4 font-medium">Customer</th>
                <th className="py-2 px-4 font-medium">Product</th>
                <th className="py-2 px-4 font-medium">Qty</th>
                <th className="py-2 px-4 font-medium">Total</th>
                <th className="py-2 px-4 font-medium">Delivery</th>
                <th className="py-2 px-4 font-medium">Status</th>
                <th className="py-2 px-4 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E0CC]">
              {orders.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-[#6B6B6B]">No food orders yet.</td></tr>}
              {orders.map((o) => (
                <tr key={o.id} data-testid={`food-order-${o.id}`}>
                  <td className="py-2 px-4 text-[12px] text-[#6B6B6B]">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="py-2 px-4">
                    <div>{o.customer_name}</div>
                    <div className="text-[11px] text-[#6B6B6B]">{o.customer_phone} · {o.customer_email}</div>
                  </td>
                  <td className="py-2 px-4">{o.product_name}</td>
                  <td className="py-2 px-4 font-tabular">{o.quantity}</td>
                  <td className="py-2 px-4 font-tabular">${(o.amount_cents/100).toFixed(2)}</td>
                  <td className="py-2 px-4 text-[12px] max-w-xs">
                    {o.delivery_address}
                    {o.delivery_notes && <div className="text-[11px] text-[#6B6B6B] italic mt-0.5">Note: {o.delivery_notes}</div>}
                  </td>
                  <td className="py-2 px-4"><StatusBadge status={o.status} /></td>
                  <td className="py-2 px-4">
                    {o.status === "paid" && (
                      <Button size="sm" variant="outline" className="rounded-full h-7 text-[11px]" onClick={() => markDelivered(o.id)} data-testid={`food-deliver-${o.id}`}>
                        Mark delivered
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <FoodProductDialog open={dialogOpen} editing={editing} onClose={() => setDialogOpen(false)} onSaved={() => { setDialogOpen(false); reload(); }} />
    </div>
  );
}

function DeliveryZonePanel() {
  const [zips, setZips] = useState("");
  const [emails, setEmails] = useState("");
  const [note, setNote] = useState("");
  const [pickupEnabled, setPickupEnabled] = useState(true);
  const [pickupAddress, setPickupAddress] = useState("");
  const [pickupHours, setPickupHours] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/armstrong/admin/settings");
      setZips((data.food_allowed_zips || []).join(", "));
      setEmails((data.food_exception_emails || []).join(", "));
      setNote(data.food_zone_help_text || "");
      setPickupEnabled(data.pickup_enabled !== false);
      setPickupAddress(data.pickup_address || "");
      setPickupHours(data.pickup_hours || "");
    } catch {
      // Silently ignore on first load — empty fields are fine.
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const { data } = await api.patch("/armstrong/admin/settings", {
        food_allowed_zips: zips,
        food_exception_emails: emails,
        food_zone_help_text: note,
        pickup_enabled: pickupEnabled,
        pickup_address: pickupAddress,
        pickup_hours: pickupHours,
      });
      setZips((data.food_allowed_zips || []).join(", "));
      setEmails((data.food_exception_emails || []).join(", "));
      setPickupEnabled(data.pickup_enabled !== false);
      setPickupAddress(data.pickup_address || "");
      setPickupHours(data.pickup_hours || "");
      toast.success("Delivery zone saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const zipCount = zips
    .split(/[\s,]+/)
    .map((z) => z.split("-")[0].trim())
    .filter((z) => /^\d{5}$/.test(z)).length;

  const mapsHelperUrl = businessAddress
    ? `https://www.google.com/maps/search/${encodeURIComponent(businessAddress)}/@?z=10`
    : "";

  if (!loaded) return null;

  return (
    <form
      onSubmit={save}
      className="rounded-2xl border border-[#E8D9A6] bg-[#FBF7EC]/60 p-5"
      data-testid="delivery-zone-panel"
    >
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h2 className="font-spectral text-xl">Local delivery zone</h2>
          <p className="text-[12.5px] text-[#6B6B6B] mt-1 max-w-2xl leading-relaxed">
            Customers outside this ZIP list can&rsquo;t check out for dog food.
            Leave blank to allow everyone (no restriction).
            {zipCount > 0 && (
              <span className="ml-1 font-medium text-ink">
                {zipCount} ZIP code{zipCount === 1 ? "" : "s"} currently allowed.
              </span>
            )}
          </p>
        </div>
        <Button
          type="submit"
          disabled={busy}
          className="rounded-full bg-ink hover:bg-crimson text-white h-9"
          data-testid="zone-save-btn"
        >
          {busy ? "Saving…" : "Save zone"}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-ink">
            Allowed ZIP codes
          </Label>
          <Textarea
            value={zips}
            onChange={(e) => setZips(e.target.value)}
            rows={4}
            placeholder="19103, 19104, 19106, 19147…"
            className="bg-white font-tabular text-[13px]"
            data-testid="zone-zips-input"
          />
          <p className="text-[11px] text-[#6B6B6B] mt-1">
            Paste comma or space-separated 5-digit ZIPs. Non-5-digit entries are ignored automatically.
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              if (!window.confirm("Restore the canonical Shasta/Tehama ZIP list (incl. 96007)?\n\nThis ADDS missing canonical ZIPs but keeps any custom ZIPs you've added. Use this to fix a stale prod list without a redeploy.")) return;
              setBusy(true);
              try {
                const { data } = await api.post("/armstrong/admin/food-zips/reset-canonical", {});
                setZips((data.current || []).join(", "));
                toast.success(`Restored canonical ZIPs (${(data.current || []).length} total). 96007 ${(data.current || []).includes("96007") ? "✓" : "missing"}`);
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Restore failed");
              } finally {
                setBusy(false);
              }
            }}
            className="mt-2 text-[11px] underline text-crimson hover:text-ink disabled:opacity-50"
            data-testid="zone-restore-canonical-btn"
          >
            Restore canonical Shasta/Tehama ZIPs (incl. 96007)
          </button>
        </div>

        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-ink">
            Exception emails (override any ZIP)
          </Label>
          <Textarea
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            rows={4}
            placeholder="family@example.com, vip@example.com"
            className="bg-white text-[13px]"
            data-testid="zone-emails-input"
          />
          <p className="text-[11px] text-[#6B6B6B] mt-1">
            These customers can buy from any ZIP — useful for family, referrals, or one-off favors.
          </p>
        </div>
      </div>

      <div className="mt-4">
        <Label className="text-xs font-semibold uppercase tracking-wider text-ink">
          Out-of-zone message (optional)
        </Label>
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={500}
          placeholder="e.g. We deliver Tue/Thu within 45 min of West Philly."
          className="bg-white text-[13px]"
          data-testid="zone-note-input"
        />
      </div>

      <div className="mt-5 rounded-xl border border-[#E8E0CC] bg-white p-4" data-testid="pickup-settings-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div>
            <h3 className="font-spectral text-base">Local pickup</h3>
            <p className="text-[12px] text-[#6B6B6B] leading-relaxed">
              Lets buyers choose Pickup at checkout instead of delivery (no ZIP restriction applies).
            </p>
          </div>
          <label className="flex items-center gap-2 text-[13px] font-medium cursor-pointer">
            <input
              type="checkbox"
              checked={pickupEnabled}
              onChange={(e) => setPickupEnabled(e.target.checked)}
              className="h-4 w-4 accent-[#0E0E0E]"
              data-testid="pickup-enabled-toggle"
            />
            Pickup enabled
          </label>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-ink">
              Pickup address
            </Label>
            <Input
              value={pickupAddress}
              onChange={(e) => setPickupAddress(e.target.value)}
              maxLength={300}
              placeholder="4054 Highway 273, Anderson, CA"
              className="bg-white text-[13px]"
              data-testid="pickup-address-input"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-ink">
              Pickup hours
            </Label>
            <Input
              value={pickupHours}
              onChange={(e) => setPickupHours(e.target.value)}
              maxLength={120}
              placeholder="Contact seller"
              className="bg-white text-[13px]"
              data-testid="pickup-hours-input"
            />
          </div>
        </div>
      </div>

      <details className="mt-4 text-[12px]">
        <summary className="cursor-pointer text-gold-dark font-medium select-none">
          Need help finding ZIPs within 45 min of your business?
        </summary>
        <div className="mt-2 space-y-2 text-[#3a3a3a] bg-white border border-[#E8E0CC] rounded-lg p-3">
          <p className="leading-relaxed">
            The fastest way: enter your business address below, open the map, and
            zoom out until you can see all the towns within a 45-minute drive.
            Then look up the ZIP for each town (Google &ldquo;ZIP code [town]&rdquo;
            and paste them above.
          </p>
          <div className="flex gap-2 flex-wrap">
            <Input
              value={businessAddress}
              onChange={(e) => setBusinessAddress(e.target.value)}
              placeholder="123 Main St, Philadelphia, PA"
              className="flex-1 min-w-[200px]"
            />
            {mapsHelperUrl && (
              <a
                href={mapsHelperUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center px-3 py-2 rounded-full bg-ink text-white text-[12px] font-semibold hover:bg-crimson"
              >
                Open in Google Maps →
              </a>
            )}
          </div>
          <p className="text-[11px] text-[#6B6B6B]">
            Tip: most US counties cluster 5–15 ZIPs per 45-minute radius. You
            can always edit this list as you learn which areas convert.
          </p>
        </div>
      </details>
    </form>
  );
}



function FoodPhotoUploader({ value, onChange, testId }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const src = value ? (thumbUrl(value) || value) : null;

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const path = r.data.storage_path;
      if (!path) throw new Error("Upload failed");
      onChange(path);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div className="rounded-lg border border-dashed border-[#E8E0CC] bg-cream overflow-hidden" data-testid={testId}>
      {src ? (
        <div className="relative">
          <img src={src} alt="" className="w-full h-40 object-cover" />
          <div className="p-2 flex gap-2 bg-white border-t border-[#E8E0CC]">
            <Button type="button" size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={busy} className="flex-1">
              <Camera size={13} className="mr-1" /> {busy ? "Uploading…" : "Replace"}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => onChange("")} className="text-crimson">
              <Trash size={13} />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          disabled={busy}
          className="w-full h-40 flex flex-col items-center justify-center gap-2 text-[#6B6B6B] hover:bg-[#FBF7EC] transition-colors"
        >
          <Camera size={28} weight="duotone" className="text-gold-dark" />
          <span className="text-[12px] font-medium">{busy ? "Uploading…" : "Click to upload product photo"}</span>
          <span className="text-[10px] text-[#9A9A9A]">JPG / PNG / WEBP — up to 10MB</span>
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handle} />
    </div>
  );
}

function FoodProductCard({ product, onChanged, onEdit, onDelete }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceDraft, setPriceDraft] = useState(product.price_usd);

  const patch = async (updates) => {
    try {
      await api.patch(`/armstrong/admin/food/${product.id}`, updates);
      toast.success("Saved");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const onPhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // /upload returns { storage_path, thumb_path, … }
      const path = r.data.storage_path;
      if (!path) throw new Error("Upload failed");
      await patch({ photo_url: path });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const savePrice = async () => {
    const v = parseFloat(priceDraft);
    if (Number.isNaN(v) || v <= 0) {
      toast.error("Enter a positive price");
      setPriceDraft(product.price_usd);
      setEditingPrice(false);
      return;
    }
    if (v === product.price_usd) {
      setEditingPrice(false);
      return;
    }
    await patch({ price_usd: v });
    setEditingPrice(false);
  };

  const photoSrc = product.photo_url ? (thumbUrl(product.photo_url) || product.photo_url) : null;

  return (
    <div className="bg-white rounded-xl border border-[#E8E0CC] overflow-hidden flex flex-col" data-testid={`food-product-${product.id}`}>
      {/* Photo with hover-to-upload affordance */}
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="relative w-full h-40 bg-cream border-b border-[#E8E0CC] group overflow-hidden focus:outline-none focus:ring-2 focus:ring-gold"
        data-testid={`food-photo-btn-${product.id}`}
      >
        {photoSrc ? (
          <img src={photoSrc} alt={product.name} className="w-full h-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-[#6B6B6B] text-[12px]">
            <Camera size={28} weight="duotone" className="mr-2 text-gold-dark" /> Click to upload photo
          </span>
        )}
        <span className={`absolute inset-0 flex items-center justify-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white bg-black/55 ${photoSrc ? "opacity-0 group-hover:opacity-100" : "opacity-0"} transition-opacity`}>
          <Camera size={14} weight="fill" /> {uploading ? "Uploading…" : "Replace photo"}
        </span>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPhotoSelected}
          data-testid={`food-photo-input-${product.id}`}
        />
      </button>

      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-spectral text-lg leading-tight truncate">{product.name}</h3>
            <p className="text-[12px] text-[#6B6B6B]">{product.bag_size || ""}</p>
          </div>
          {/* Inline-edit price */}
          {editingPrice ? (
            <div className="flex items-center gap-1">
              <span className="text-[#6B6B6B]">$</span>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                autoFocus
                value={priceDraft}
                onChange={(e) => setPriceDraft(e.target.value)}
                onBlur={savePrice}
                onKeyDown={(e) => {
                  if (e.key === "Enter") savePrice();
                  if (e.key === "Escape") { setPriceDraft(product.price_usd); setEditingPrice(false); }
                }}
                className="h-8 w-20 text-right font-tabular"
                data-testid={`food-price-input-${product.id}`}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { setPriceDraft(product.price_usd); setEditingPrice(true); }}
              className="font-tabular text-lg text-ink hover:text-gold-dark transition-colors flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-dashed border-[#E8D9A6] hover:border-gold-dark hover:bg-[#FBF7EC]"
              data-testid={`food-price-${product.id}`}
              title="Click to edit price"
            >
              ${product.price_usd.toFixed(2)}
              <PencilSimple size={11} weight="bold" className="text-gold-dark" />
            </button>
          )}
        </div>

        <div className="mt-3 flex items-center justify-between text-xs">
          <Badge
            variant={product.in_stock ? "default" : "secondary"}
            className={`cursor-pointer ${product.in_stock ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200" : "hover:bg-gray-200"}`}
            onClick={() => patch({ in_stock: !product.in_stock })}
            data-testid={`food-stock-toggle-${product.id}`}
            title="Click to toggle"
          >
            {product.in_stock ? "In stock" : "Hidden"}
          </Badge>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={onEdit} className="h-7 text-[12px]" data-testid={`food-edit-${product.id}`}>
              Edit details
            </Button>
            <Button size="sm" variant="ghost" onClick={onDelete} className="h-7 text-[12px] text-crimson" data-testid={`food-delete-${product.id}`}>
              <Trash size={12} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FoodProductDialog({ open, editing, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "", price_usd: "", photo_url: "", bag_size: "", in_stock: true });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing || { name: "", description: "", price_usd: "", photo_url: "", bag_size: "", in_stock: true });
    }
  }, [open, editing]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, price_usd: parseFloat(form.price_usd) };
      if (editing) {
        await api.patch(`/armstrong/admin/food/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/armstrong/admin/food", payload);
        toast.success("Created");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="food-product-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit product" : "New food product"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="food-product-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Price (USD)</Label>
              <Input required type="number" step="0.01" min="0.01" value={form.price_usd} onChange={(e) => setForm({...form, price_usd: e.target.value})} data-testid="food-product-price" />
            </div>
            <div>
              <Label className="text-xs">Bag size</Label>
              <Input value={form.bag_size || ""} onChange={(e) => setForm({...form, bag_size: e.target.value})} placeholder="30 lb bag" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Photo</Label>
            <FoodPhotoUploader
              value={form.photo_url}
              onChange={(p) => setForm({...form, photo_url: p})}
              testId="food-product-photo-uploader"
            />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea value={form.description || ""} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} />
          </div>
          <div className="flex items-center gap-2">
            <input id="in_stock" type="checkbox" checked={form.in_stock} onChange={(e) => setForm({...form, in_stock: e.target.checked})} />
            <Label htmlFor="in_stock" className="text-sm">In stock (visible to customers)</Label>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-full bg-gold-gradient text-ink font-semibold w-full" data-testid="food-product-submit">
              {busy ? "Saving…" : editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================== Merch
function MerchTab() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all"); // all | published | hidden
  const [search, setSearch] = useState("");

  const reload = async () => {
    const [p, o] = await Promise.all([
      api.get("/armstrong/admin/merch"),
      api.get("/armstrong/admin/orders?kind=merch"),
    ]);
    setProducts(p.data.products || []);
    setOrders(o.data.orders || []);
  };
  useEffect(() => { reload(); }, []);

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/armstrong/admin/merch/sync");
      toast.success(`Synced ${data.synced} products (skipped ${data.skipped})`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const retry = async (id) => {
    try {
      await api.post(`/armstrong/admin/merch/orders/${id}/retry-printify`);
      toast.success("Resubmitted to Printify");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Retry failed");
    }
  };

  const togglePublished = async (printifyId, next) => {
    // Optimistic UI update so the toggle feels instant on 141 products.
    setProducts((rows) => rows.map((r) => r.printify_id === printifyId ? { ...r, published: next } : r));
    try {
      await api.patch(`/armstrong/admin/merch/${printifyId}`, { published: next });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
      reload();
    }
  };

  const toggleCustomizable = async (printifyId, next) => {
    // `next` is the effective customizable flag the admin wants. We
    // translate to `customizable_override`: null = follow auto-detect,
    // true = force on, false = force off. To keep the UI simple we
    // always set an explicit boolean override here so the admin's
    // choice sticks across re-syncs.
    setProducts((rows) =>
      rows.map((r) => (
        r.printify_id === printifyId
          ? { ...r, customizable: next, customizable_override: next }
          : r
      ))
    );
    try {
      await api.patch(`/armstrong/admin/merch/${printifyId}`, { customizable_override: next });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
      reload();
    }
  };

  // Reorder mode — when on, the product grid renders a vertical list
  // with up/down + drag handles for choosing the storefront sort.
  // Saved as a single `/admin/merch/reorder` bulk call when the admin
  // clicks "Save order". Up/down buttons exist for accessibility +
  // touch devices that can't drag reliably.
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderRows, setReorderRows] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);

  const enterReorder = () => {
    // Snapshot only the published products — hidden ones don't show
    // on /armstrong so their order doesn't matter. Sorted by current
    // featured_order so the admin starts from the current state.
    const snap = products
      .filter((p) => p.published !== false)
      .sort((a, b) => (a.featured_order ?? 999) - (b.featured_order ?? 999) || (a.title || "").localeCompare(b.title || ""));
    setReorderRows(snap);
    setReorderMode(true);
  };

  const cancelReorder = () => { setReorderMode(false); setReorderRows([]); };

  const moveRow = (from, to) => {
    if (to < 0 || to >= reorderRows.length || from === to) return;
    setReorderRows((rows) => {
      const next = rows.slice();
      const [pulled] = next.splice(from, 1);
      next.splice(to, 0, pulled);
      return next;
    });
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      const payload = {
        order: reorderRows.map((p, idx) => ({ printify_id: p.printify_id, featured_order: idx })),
      };
      await api.post("/armstrong/admin/merch/reorder", payload);
      toast.success(`Saved order for ${reorderRows.length} products`);
      setReorderMode(false);
      setReorderRows([]);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSavingOrder(false); }
  };

  const bulkPublish = async (nextPublished) => {
    const targets = visibleRows.map((p) => p.printify_id);
    if (targets.length === 0) return;
    if (!window.confirm(`${nextPublished ? "Publish" : "Hide"} ${targets.length} ${targets.length === 1 ? "product" : "products"}?`)) return;
    try {
      await api.post("/armstrong/admin/merch/bulk-publish", { printify_ids: targets, published: nextPublished });
      toast.success(`${nextPublished ? "Published" : "Hidden"} ${targets.length}`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk update failed");
    }
  };

  const visibleRows = products.filter((p) => {
    if (filter === "published" && !p.published) return false;
    if (filter === "hidden" && p.published) return false;
    if (search && !(p.title || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const publishedCount = products.filter((p) => p.published).length;

  return (
    <div className="space-y-8">
      <section>
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="font-spectral text-2xl">Printify merch</h2>
            <p className="text-sm text-[#6B6B6B] mt-1">
              {publishedCount} of {products.length} products visible on <Link to="/armstrong" className="underline">/armstrong</Link>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!reorderMode && (
              <Button
                variant="outline"
                className="rounded-full"
                onClick={enterReorder}
                disabled={busy || products.filter((p) => p.published !== false).length === 0}
                data-testid="merch-reorder-toggle"
              >
                Reorder storefront →
              </Button>
            )}
            <Button className="rounded-full bg-ink hover:bg-crimson text-white" disabled={busy || reorderMode} onClick={sync} data-testid="merch-sync-btn">
              <ArrowsClockwise size={16} className={`mr-1 ${busy ? "animate-spin" : ""}`} /> {busy ? "Syncing…" : "Re-sync from Printify"}
            </Button>
          </div>
        </div>
        <p className="text-[12px] text-[#6B6B6B] mb-5">
          Toggle the switch on each card to show or hide it on the public storefront. Your toggles are preserved across re-syncs — Printify changes won't override what you've curated.
        </p>

        {/* Either reorder UI or normal product grid */}
        {reorderMode ? (
          <MerchReorderList
            rows={reorderRows}
            onMove={moveRow}
            onDrop={(from, to) => moveRow(from, to)}
            onCancel={cancelReorder}
            onSave={saveOrder}
            saving={savingOrder}
          />
        ) : (
          <>
            {/* Filter & search controls */}
            <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="merch-controls">
              <div className="flex gap-1 rounded-full bg-cream p-1">
                {["all", "published", "hidden"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`text-[12px] font-medium px-3 py-1 rounded-full transition-colors ${
                      filter === f ? "bg-ink text-white" : "text-[#6B6B6B] hover:text-ink"
                    }`}
                    data-testid={`merch-filter-${f}`}
                  >
                    {f === "all" ? `All (${products.length})` : f === "published" ? `Published (${publishedCount})` : `Hidden (${products.length - publishedCount})`}
                  </button>
                ))}
              </div>
              <Input
                placeholder="Search by title…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-xs h-9 text-sm"
                data-testid="merch-search"
              />
              {visibleRows.length > 0 && (
                <div className="ml-auto flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => bulkPublish(true)} className="rounded-full h-9 text-[12px]" data-testid="merch-bulk-publish">
                    Publish {visibleRows.length}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => bulkPublish(false)} className="rounded-full h-9 text-[12px] text-crimson border-crimson/40" data-testid="merch-bulk-hide">
                    Hide {visibleRows.length}
                  </Button>
                </div>
              )}
            </div>

            {/* Product grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {visibleRows.length === 0 && <p className="text-sm text-[#6B6B6B] col-span-full py-8 text-center">No products match.</p>}
              {visibleRows.map((p) => (
                <MerchAdminCard key={p.printify_id} product={p} onToggle={togglePublished} onToggleCustomizable={toggleCustomizable} />
              ))}
            </div>
          </>
        )}
      </section>

      <section>
        <h2 className="font-spectral text-2xl mb-4">Merch orders</h2>
        <div className="rounded-2xl border border-[#E8E0CC] bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF3DA] text-left">
              <tr>
                <th className="py-2 px-4 font-medium">When</th>
                <th className="py-2 px-4 font-medium">Customer</th>
                <th className="py-2 px-4 font-medium">Product / variant</th>
                <th className="py-2 px-4 font-medium">Qty</th>
                <th className="py-2 px-4 font-medium">Total</th>
                <th className="py-2 px-4 font-medium">Printify</th>
                <th className="py-2 px-4 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E0CC]">
              {orders.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-[#6B6B6B]">No merch orders yet.</td></tr>}
              {orders.map((o) => (
                <tr key={o.id} data-testid={`merch-order-${o.id}`}>
                  <td className="py-2 px-4 text-[12px] text-[#6B6B6B]">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="py-2 px-4">
                    <div>{o.customer_name}</div>
                    <div className="text-[11px] text-[#6B6B6B]">{o.customer_email}</div>
                  </td>
                  <td className="py-2 px-4">
                    <div className="line-clamp-1">{o.product_title}</div>
                    <div className="text-[11px] text-[#6B6B6B]">{o.variant_title}</div>
                  </td>
                  <td className="py-2 px-4 font-tabular">{o.quantity}</td>
                  <td className="py-2 px-4 font-tabular">${(o.amount_cents/100).toFixed(2)}</td>
                  <td className="py-2 px-4">
                    {o.printify_order_id ? (
                      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">
                        <CheckCircle size={10} weight="fill" className="mr-1" /> Submitted
                      </Badge>
                    ) : o.status === "paid" ? (
                      <Badge variant="destructive" className="text-[10px]">Failed</Badge>
                    ) : (
                      <span className="text-[11px] text-[#6B6B6B]">awaiting payment</span>
                    )}
                    {o.printify_error && <div className="text-[10px] text-crimson mt-1">{o.printify_error}</div>}
                  </td>
                  <td className="py-2 px-4">
                    {o.status === "paid" && !o.printify_order_id && (
                      <Button size="sm" variant="outline" className="rounded-full h-7 text-[11px]" onClick={() => retry(o.id)} data-testid={`merch-retry-${o.id}`}>
                        Retry Printify
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function MerchAdminCard({ product, onToggle, onToggleCustomizable }) {
  const img = product.images?.[0]?.src;
  const cents = product.min_price_cents || 0;
  const customizable = !!product.customizable;
  return (
    <div
      className={`bg-white rounded-xl border overflow-hidden flex flex-col transition-all ${
        product.published ? "border-[#E8E0CC]" : "border-[#E5E5E5] opacity-60 hover:opacity-90"
      }`}
      data-testid={`merch-admin-card-${product.printify_id}`}
    >
      {img ? (
        <img src={img} alt={product.title} className="w-full aspect-square object-cover" />
      ) : (
        <div className="w-full aspect-square bg-cream flex items-center justify-center">
          <TShirt size={32} weight="duotone" className="text-gold" />
        </div>
      )}
      <div className="p-3 flex-1 flex flex-col">
        <h3 className="text-[12px] font-medium leading-tight line-clamp-2 mb-1">{product.title}</h3>
        <p className="text-[11px] text-[#6B6B6B] font-tabular mb-3">
          From ${(cents/100).toFixed(2)} · {(product.variants || []).length} variants
        </p>
        <div className="mt-auto space-y-2">
          <label className="flex items-center justify-between gap-2 cursor-pointer group" data-testid={`merch-publish-toggle-${product.printify_id}`}>
            <span className={`text-[10px] font-bold uppercase tracking-[0.12em] ${product.published ? "text-emerald-700" : "text-[#9A9A9A]"}`}>
              {product.published ? "Visible" : "Hidden"}
            </span>
            <span className={`relative inline-block h-5 w-9 rounded-full transition-colors ${product.published ? "bg-emerald-500" : "bg-gray-300"}`}>
              <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${product.published ? "translate-x-4" : "translate-x-0.5"}`} />
            </span>
            <input
              type="checkbox"
              className="sr-only"
              checked={!!product.published}
              onChange={(e) => onToggle(product.printify_id, e.target.checked)}
            />
          </label>
          {onToggleCustomizable && (
            <label
              className="flex items-center justify-between gap-2 cursor-pointer"
              data-testid={`merch-customize-toggle-${product.printify_id}`}
            >
              <span className={`text-[10px] font-bold uppercase tracking-[0.12em] flex items-center gap-1 ${customizable ? "text-gold-dark" : "text-[#9A9A9A]"}`}>
                <PaintBrush size={10} weight="fill" /> Personalize
              </span>
              <span className={`relative inline-block h-5 w-9 rounded-full transition-colors ${customizable ? "bg-gold-gradient" : "bg-gray-300"}`}>
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${customizable ? "translate-x-4" : "translate-x-0.5"}`} />
              </span>
              <input
                type="checkbox"
                className="sr-only"
                checked={customizable}
                onChange={(e) => onToggleCustomizable(product.printify_id, e.target.checked)}
              />
            </label>
          )}
        </div>
      </div>
    </div>
  );
}

// =========================================================== shared
function StatusBadge({ status }) {
  const map = {
    pending: { color: "bg-amber-100 text-amber-800", label: "Pending payment" },
    active: { color: "bg-emerald-100 text-emerald-800", label: "Active" },
    paused: { color: "bg-blue-100 text-blue-800", label: "Paused" },
    cancelling: { color: "bg-orange-100 text-orange-800", label: "Cancelling at period end" },
    cancelled: { color: "bg-gray-100 text-gray-800", label: "Cancelled" },
    pending_payment: { color: "bg-amber-100 text-amber-800", label: "Pending payment" },
    paid: { color: "bg-emerald-100 text-emerald-800", label: "Paid" },
    delivered: { color: "bg-blue-100 text-blue-800", label: "Delivered" },
  };
  const m = map[status] || { color: "bg-gray-100 text-gray-700", label: status };
  return <span className={`text-[10px] font-bold uppercase tracking-[0.1em] rounded-full px-2 py-0.5 ${m.color}`}>{m.label}</span>;
}


// ============================================================== CustomOrdersTab
/**
 * Admin view of every custom-merch inquiry submitted from the public
 * `/armstrong` storefront. Pipeline:
 *
 *   requested  →  quoted (admin sends mockup + price)
 *                   ↓
 *                paid (Stripe webhook flips this)
 *                   ↓
 *                fulfilled (admin marks shipped)
 *
 *   declined (terminal, with optional reason)
 *
 * One row per order. Click "Quote" to send the mockup + price; the
 * buyer receives an email + their /armstrong/custom/:id page updates.
 */
function CustomOrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteTarget, setQuoteTarget] = useState(null);
  const [declineTarget, setDeclineTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/armstrong/admin/custom-orders");
      setOrders(data.orders || []);
    } catch {
      toast.error("Failed to load custom orders");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markFulfilled = async (id) => {
    if (!window.confirm("Mark this order as fulfilled (shipped)?")) return;
    try {
      await api.post(`/armstrong/admin/custom-orders/${id}/mark-fulfilled`);
      toast.success("Marked fulfilled");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (loading) return <p className="text-sm text-[#6B6B6B]">Loading…</p>;

  const buckets = {
    open: orders.filter((o) => ["requested", "quoted", "mockup_sent"].includes(o.status)),
    paid: orders.filter((o) => o.status === "paid"),
    closed: orders.filter((o) => ["fulfilled", "declined"].includes(o.status)),
  };

  return (
    <div className="space-y-8" data-testid="custom-orders-tab">
      <div className="rounded-2xl border border-[#E8E0CC] bg-cream/60 px-5 py-4 flex items-start gap-3 flex-wrap">
        <PaintBrush size={28} weight="duotone" className="text-gold-dark flex-shrink-0" />
        <div className="flex-1 min-w-[240px]">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold-dark">
            Custom orders flow
          </p>
          <p className="text-[13px] text-[#3a3a3a] mt-1 leading-relaxed">
            Customers submit a request from <Link to="/armstrong#merch" className="underline">/armstrong</Link>.
            Once you upload the mockup image somewhere (Imgur, Drive, Printify mockup link…) and
            click <strong>Quote</strong>, the buyer gets an email with a payment link.
            Mark as <strong>Fulfilled</strong> once you&rsquo;ve shipped the order.
          </p>
        </div>
      </div>

      <CustomOrderSection title="Open · awaiting action" rows={buckets.open} onQuote={setQuoteTarget} onDecline={setDeclineTarget} onMarkFulfilled={markFulfilled} testid="custom-section-open" emptyCopy="No open requests right now." />
      <CustomOrderSection title="Paid · ready to ship" rows={buckets.paid} onQuote={setQuoteTarget} onDecline={setDeclineTarget} onMarkFulfilled={markFulfilled} testid="custom-section-paid" emptyCopy="No paid orders waiting on you." />
      <CustomOrderSection title="Closed" rows={buckets.closed} onQuote={null} onDecline={null} onMarkFulfilled={null} testid="custom-section-closed" emptyCopy="No completed custom orders yet." />

      <CustomQuoteDialog
        order={quoteTarget}
        onClose={() => setQuoteTarget(null)}
        onSaved={() => { setQuoteTarget(null); load(); }}
      />
      <CustomDeclineDialog
        order={declineTarget}
        onClose={() => setDeclineTarget(null)}
        onSaved={() => { setDeclineTarget(null); load(); }}
      />
    </div>
  );
}

function CustomOrderSection({ title, rows, onQuote, onDecline, onMarkFulfilled, testid, emptyCopy }) {
  return (
    <section data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold-dark mb-3">{title} <span className="text-[#9A9A9A] font-tabular ml-1">({rows.length})</span></p>
      {rows.length === 0 ? (
        <p className="text-sm text-[#6B6B6B] italic">{emptyCopy}</p>
      ) : (
        <div className="space-y-3">
          {rows.map((o) => (
            <CustomOrderRow
              key={o.id}
              order={o}
              onQuote={onQuote ? () => onQuote(o) : null}
              onDecline={onDecline ? () => onDecline(o) : null}
              onMarkFulfilled={onMarkFulfilled ? () => onMarkFulfilled(o.id) : null}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function CustomOrderRow({ order, onQuote, onDecline, onMarkFulfilled }) {
  const created = new Date(order.created_at).toLocaleString();
  return (
    <div
      className="rounded-xl border border-[#E8E0CC] bg-white p-4"
      data-testid={`custom-order-${order.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-spectral text-lg">{order.customer_name}</h3>
            <CustomStatusBadge status={order.status} />
          </div>
          <p className="text-[12px] text-[#6B6B6B] mt-0.5">
            <a href={`mailto:${order.customer_email}`} className="hover:text-ink underline decoration-[#E5E5E5]">{order.customer_email}</a>
            {order.customer_phone ? ` · ${order.customer_phone}` : ""}
            {" · "}{created}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Qty</p>
          <p className="font-tabular text-[16px]">{order.quantity}</p>
        </div>
      </div>
      <div className="mt-3 grid sm:grid-cols-3 gap-3 text-[12.5px]">
        <div className="sm:col-span-2">
          <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Description</p>
          <p className="mt-0.5 whitespace-pre-wrap leading-relaxed">{order.description}</p>
        </div>
        <div className="space-y-2">
          {order.product_reference && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Base item</p>
              <p className="mt-0.5">{order.product_reference}</p>
            </div>
          )}
          {order.desired_by && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Needed by</p>
              <p className="mt-0.5">{order.desired_by}</p>
            </div>
          )}
          {order.inspiration_url && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Inspiration</p>
              <a href={order.inspiration_url} target="_blank" rel="noreferrer" className="text-gold-dark underline break-all">
                {order.inspiration_url}
              </a>
            </div>
          )}
        </div>
      </div>
      {order.mockup_url && (
        <div className="mt-3">
          <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A] mb-1">Mockup sent</p>
          <img src={order.mockup_url} alt="" className="max-h-32 rounded-md object-contain border border-[#E8E0CC] bg-cream p-1" />
        </div>
      )}
      {order.quoted_price_usd && (
        <div className="mt-3 text-[12.5px]">
          <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Quote</p>
          <p className="font-tabular mt-0.5">${order.quoted_price_usd.toFixed(2)} × {order.quantity} = ${(order.quoted_price_usd * order.quantity).toFixed(2)}</p>
        </div>
      )}
      {order.admin_notes && order.status === "declined" && (
        <p className="mt-3 text-[12.5px] text-crimson">Decline reason: {order.admin_notes}</p>
      )}
      <div className="mt-4 flex gap-2 flex-wrap">
        <CustomOrderShareButton order={order} />
        {onQuote && order.status !== "paid" && order.status !== "fulfilled" && order.status !== "declined" && (
          <Button
            size="sm"
            onClick={onQuote}
            className="rounded-full bg-ink hover:bg-crimson text-white h-8 px-4 text-[12px]"
            data-testid={`custom-quote-${order.id}`}
          >
            {order.quoted_price_usd ? "Update mockup / price" : "Send mockup + price"}
          </Button>
        )}
        {onMarkFulfilled && order.status === "paid" && (
          <Button
            size="sm"
            variant="outline"
            onClick={onMarkFulfilled}
            className="rounded-full h-8 px-4 text-[12px]"
            data-testid={`custom-fulfill-${order.id}`}
          >
            Mark fulfilled
          </Button>
        )}
        {onDecline && ["requested", "quoted", "mockup_sent"].includes(order.status) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onDecline}
            className="rounded-full h-8 px-4 text-[12px] text-crimson hover:text-crimson"
            data-testid={`custom-decline-${order.id}`}
          >
            Decline
          </Button>
        )}
        <a
          href={`/armstrong/custom/${order.id}`}
          target="_blank"
          rel="noreferrer"
          className="text-[12px] text-[#6B6B6B] hover:text-ink underline self-center"
        >
          Buyer view ↗
        </a>
      </div>
    </div>
  );
}


// ============================================================ CustomOrderShareButton
/**
 * One-tap share menu for a custom order's public buyer page. Mirrors
 * the training `ShareLinkPanel` pattern (QR + SMS + email + native
 * share + copy) but the message bodies are status-aware:
 *
 *   • requested  — "Save this link — we'll send your mockup here in ≤ 5 days"
 *   • quoted     — "Your mockup is ready! Review and pay here"
 *   • paid       — "Order receipt + tracking will update here"
 *
 * Keeps the husband's flow fast: he can text the link from his phone
 * in seconds without re-typing anything.
 */
function CustomOrderShareButton({ order }) {
  const [open, setOpen] = useState(false);
  const origin = typeof window !== "undefined" ? webOrigin() : "";
  const url = `${origin}/armstrong/custom/${order.id}`;
  const firstName = (order.customer_name || "there").split(" ")[0];

  const isQuoted = order.status === "quoted" || order.status === "mockup_sent";
  const isPaid = order.status === "paid" || order.status === "fulfilled";
  const isDeclined = order.status === "declined";

  let smsBody;
  let emailSubject;
  let emailBody;
  if (isQuoted) {
    const total = ((order.quoted_price_usd || 0) * (order.quantity || 1)).toFixed(2);
    smsBody = `Hi ${firstName}, your Armstrong custom mockup is ready! Review and pay ($${total}) here: ${url}`;
    emailSubject = "Your custom-order mockup is ready";
    emailBody = `Hi ${firstName},\n\nYour mockup is ready. Review the design and pay here:\n\n${url}\n\nTotal: $${total} (${order.quantity} × $${(order.quoted_price_usd || 0).toFixed(2)})\n\nReply if you want any tweaks before paying.\n\n— Armstrong Supreme Team K9`;
  } else if (isPaid) {
    smsBody = `Hi ${firstName}, your Armstrong custom order is paid and in production. Track updates here: ${url}`;
    emailSubject = "Your custom-order page";
    emailBody = `Hi ${firstName},\n\nYour custom order is being prepared. Bookmark this page for updates:\n\n${url}\n\n— Armstrong Supreme Team K9`;
  } else if (isDeclined) {
    smsBody = `Hi ${firstName}, your custom-order page is here: ${url}`;
    emailSubject = "Your custom-order page";
    emailBody = `Hi ${firstName},\n\nFor your records, here's the page for your request:\n\n${url}\n\n— Armstrong Supreme Team K9`;
  } else {
    smsBody = `Hi ${firstName}, we got your Armstrong custom-order request. We'll send your mockup to this page within 5 business days — save the link: ${url}`;
    emailSubject = "Your custom-order page";
    emailBody = `Hi ${firstName},\n\nThanks for the custom-order request. We'll have a mockup for you within 5 business days. Bookmark this page — that's where the mockup, price, and payment link will land:\n\n${url}\n\n— Armstrong Supreme Team K9`;
  }

  const copy = () => { navigator.clipboard.writeText(url); toast.success("Link copied"); };
  const sendSms = () => { window.location.href = `sms:?&body=${encodeURIComponent(smsBody)}`; };
  const sendEmail = () => {
    const href = `mailto:${encodeURIComponent(order.customer_email || "")}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    window.location.href = href;
  };
  const nativeShare = async () => {
    if (!navigator.share) { copy(); return; }
    try {
      await navigator.share({ title: "Your Armstrong custom order", text: smsBody, url });
    } catch (_e) { /* user cancelled */ }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="rounded-full h-8 px-3 text-[12px]"
        data-testid={`custom-share-${order.id}`}
      >
        <ShareNetwork size={12} weight="duotone" className="mr-1" /> Share link
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid={`custom-share-dialog-${order.id}`}>
          <DialogHeader>
            <DialogTitle>Share buyer page · {order.customer_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[13px] text-[#3a3a3a]">
              Send this link to {order.customer_name}. The page updates automatically as you send the
              mockup, accept payment, and ship — they only ever need this one URL.
            </p>
            <div className="flex items-center gap-4 rounded-xl border border-[#E8E0CC] bg-cream p-4">
              <div className="bg-white rounded-lg p-2 border border-[#E8E0CC] flex-shrink-0">
                <QRCodeSVG value={url} size={88} bgColor="#FFFFFF" fgColor="#0E0E0E" level="M" />
              </div>
              <div className="text-[12px] text-[#3a3a3a] leading-relaxed">
                <strong className="text-ink">Scan with phone camera.</strong><br />
                Show this if {firstName} is in front of you, or text the link below.
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={sendSms} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="custom-share-sms-btn">
                <ChatCircle size={14} weight="duotone" className="mr-1.5" /> Text it
              </Button>
              <Button onClick={sendEmail} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="custom-share-email-btn">
                <EnvelopeSimple size={14} weight="duotone" className="mr-1.5" /> Email it
              </Button>
              <Button onClick={nativeShare} variant="outline" className="rounded-full border-ink" data-testid="custom-share-native-btn">
                <ShareNetwork size={14} weight="duotone" className="mr-1.5" /> Share via…
              </Button>
              <Button onClick={copy} variant="outline" className="rounded-full" data-testid="custom-share-copy-btn">
                <Copy size={14} weight="duotone" className="mr-1.5" /> Copy link
              </Button>
            </div>
            <details className="rounded-lg border border-[#E8E0CC] bg-white px-3 py-2">
              <summary className="cursor-pointer text-[11px] text-[#6B6B6B] hover:text-ink select-none">
                Show raw URL &amp; SMS preview
              </summary>
              <div className="mt-2 break-all text-[11px] font-mono text-[#3a3a3a] bg-cream p-2 rounded">{url}</div>
              <div className="mt-2 p-2 rounded bg-cream border border-[#E8E0CC]">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B] mb-1">SMS preview</p>
                <p className="text-[12px] text-[#3a3a3a] leading-relaxed whitespace-pre-line">{smsBody}</p>
              </div>
            </details>
            <Button variant="outline" className="w-full rounded-full" onClick={() => setOpen(false)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CustomStatusBadge({ status }) {
  const map = {
    requested: { cls: "bg-amber-100 text-amber-900", label: "Awaiting mockup", Icon: Hourglass },
    quoted: { cls: "bg-[#FBF7EC] text-ink", label: "Quote sent", Icon: PaintBrush },
    mockup_sent: { cls: "bg-[#FBF7EC] text-ink", label: "Quote sent", Icon: PaintBrush },
    paid: { cls: "bg-emerald-100 text-emerald-900", label: "Paid", Icon: CheckCircle },
    fulfilled: { cls: "bg-emerald-100 text-emerald-900", label: "Fulfilled", Icon: CheckCircle },
    declined: { cls: "bg-crimson/10 text-crimson", label: "Declined", Icon: XCircle },
  };
  const m = map[status] || { cls: "bg-gray-100 text-gray-700", label: status, Icon: PaintBrush };
  const I = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] rounded-full px-2 py-0.5 ${m.cls}`}>
      <I size={10} weight="fill" /> {m.label}
    </span>
  );
}

function CustomQuoteDialog({ order, onClose, onSaved }) {
  const [form, setForm] = useState({ quoted_price_usd: "", mockup_url: "", admin_notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (order) {
      setForm({
        quoted_price_usd: order.quoted_price_usd || "",
        mockup_url: order.mockup_url || "",
        admin_notes: order.admin_notes || "",
      });
    }
  }, [order]);

  if (!order) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/armstrong/admin/custom-orders/${order.id}/quote`, {
        quoted_price_usd: parseFloat(form.quoted_price_usd),
        mockup_url: form.mockup_url.trim(),
        admin_notes: form.admin_notes.trim() || null,
      });
      toast.success("Quote sent — buyer has been emailed.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="custom-quote-dialog">
        <DialogHeader>
          <DialogTitle>Send mockup + price to {order.customer_name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Mockup image URL</Label>
            <Input
              required
              value={form.mockup_url}
              onChange={(e) => setForm({ ...form, mockup_url: e.target.value })}
              placeholder="https://printify.com/mockups/... or any public image link"
              data-testid="custom-quote-mockup"
            />
            <p className="text-[10.5px] text-[#9A9A9A] mt-1">
              Paste a URL from Printify, Imgur, Drive, or anywhere public. We&rsquo;ll show it on the buyer&rsquo;s page.
            </p>
          </div>
          <div>
            <Label className="text-xs">Per-unit price (USD)</Label>
            <Input
              required
              type="number"
              step="0.01"
              min="0.01"
              max="10000"
              value={form.quoted_price_usd}
              onChange={(e) => setForm({ ...form, quoted_price_usd: e.target.value })}
              data-testid="custom-quote-price"
            />
            <p className="text-[10.5px] text-[#9A9A9A] mt-1">
              Will be charged × {order.quantity} when buyer pays.
            </p>
          </div>
          <div>
            <Label className="text-xs">Notes for the buyer (optional)</Label>
            <Textarea
              rows={3}
              value={form.admin_notes}
              onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
              placeholder="Any context about the mockup, lead time, materials, etc."
              data-testid="custom-quote-notes"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-full bg-ink hover:bg-crimson text-white w-full" data-testid="custom-quote-submit">
              {busy ? "Sending…" : "Send to buyer →"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CustomDeclineDialog({ order, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!order) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/armstrong/admin/custom-orders/${order.id}/decline`, { reason });
      toast.success("Buyer has been notified");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="custom-decline-dialog">
        <DialogHeader>
          <DialogTitle>Decline request from {order.customer_name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Reason (shown to buyer)</Label>
            <Textarea
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="We can't take this on right now because…"
              data-testid="custom-decline-reason"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} variant="destructive" className="rounded-full w-full" data-testid="custom-decline-submit">
              {busy ? "Sending…" : "Decline and notify buyer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================== MerchReorderList
/**
 * Drag-and-drop reordering list for the Armstrong merch storefront.
 *
 * Native HTML5 drag-and-drop (no library) — works in every modern
 * desktop browser. Each row also has explicit ↑ ↓ buttons so touch
 * devices, keyboard users, and "I just want to bump this one item to
 * the top" cases have a non-drag path. Position 0 = appears first on
 * `/armstrong`; the parent component persists with one bulk POST.
 */
function MerchReorderList({ rows, onMove, onDrop, onCancel, onSave, saving }) {
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const handleDragStart = (i) => () => setDraggedIdx(i);
  const handleDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTarget(i);
  };
  const handleDragLeave = () => setDropTarget(null);
  const handleDropOn = (i) => (e) => {
    e.preventDefault();
    if (draggedIdx !== null && draggedIdx !== i) onDrop(draggedIdx, i);
    setDraggedIdx(null);
    setDropTarget(null);
  };
  const handleDragEnd = () => { setDraggedIdx(null); setDropTarget(null); };

  return (
    <div data-testid="merch-reorder-list">
      <div className="sticky top-0 z-10 -mx-3 px-3 py-2.5 mb-3 bg-cream/95 backdrop-blur rounded-t-lg border-b border-gold-dark/30 flex items-center gap-3 flex-wrap">
        <p className="text-[12px] text-[#3a3a3a] flex-1">
          <strong>Drag</strong> rows to reorder, or use the <strong>↑ ↓</strong> buttons. Position 1 appears first on the storefront.
        </p>
        <Button size="sm" variant="ghost" onClick={onCancel} className="rounded-full h-8 px-4 text-[12px]" disabled={saving} data-testid="merch-reorder-cancel">
          Cancel
        </Button>
        <Button
          size="sm"
          className="rounded-full bg-ink hover:bg-crimson text-white h-8 px-4 text-[12px]"
          onClick={onSave}
          disabled={saving}
          data-testid="merch-reorder-save"
        >
          {saving ? "Saving…" : `Save order for ${rows.length}`}
        </Button>
      </div>
      <div className="space-y-1.5 max-h-[70vh] overflow-y-auto pr-1">
        {rows.length === 0 && (
          <p className="text-sm text-[#6B6B6B] py-8 text-center">No published products to reorder.</p>
        )}
        {rows.map((p, i) => {
          const isDragging = draggedIdx === i;
          const isOver = dropTarget === i && draggedIdx !== null && draggedIdx !== i;
          const img = p.images?.[0]?.src;
          return (
            <div
              key={p.printify_id}
              draggable
              onDragStart={handleDragStart(i)}
              onDragOver={handleDragOver(i)}
              onDragLeave={handleDragLeave}
              onDrop={handleDropOn(i)}
              onDragEnd={handleDragEnd}
              className={`flex items-center gap-3 rounded-lg border bg-white p-2 select-none transition-all ${
                isDragging ? "opacity-40" : ""
              } ${isOver ? "border-gold-dark border-2 bg-[#FBF7EC]" : "border-[#E8E0CC]"}`}
              data-testid={`merch-reorder-row-${p.printify_id}`}
            >
              {/* Drag handle */}
              <div className="cursor-grab active:cursor-grabbing px-1 text-[#9A9A9A]" aria-label="Drag to reorder">
                ⋮⋮
              </div>
              {/* Position number */}
              <div className="font-mono text-[12px] text-[#9A9A9A] w-7 text-right tabular-nums">{i + 1}</div>
              {/* Thumb */}
              {img ? (
                <img src={img} alt="" className="w-10 h-10 rounded object-cover bg-cream flex-shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded bg-cream flex items-center justify-center flex-shrink-0">
                  <TShirt size={16} weight="duotone" className="text-gold" />
                </div>
              )}
              {/* Title + meta */}
              <div className="flex-1 min-w-0">
                <p className="text-[13px] leading-tight line-clamp-1">{p.title}</p>
                <p className="text-[10.5px] text-[#9A9A9A] font-mono">From ${((p.min_price_cents || 0) / 100).toFixed(2)}</p>
              </div>
              {/* Up / down buttons */}
              <div className="flex flex-col gap-0.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => onMove(i, i - 1)}
                  disabled={i === 0}
                  className="h-5 w-7 rounded text-[11px] bg-cream hover:bg-gold-gradient hover:text-ink disabled:opacity-30 transition-colors"
                  aria-label="Move up"
                  data-testid={`merch-reorder-up-${p.printify_id}`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => onMove(i, i + 1)}
                  disabled={i === rows.length - 1}
                  className="h-5 w-7 rounded text-[11px] bg-cream hover:bg-gold-gradient hover:text-ink disabled:opacity-30 transition-colors"
                  aria-label="Move down"
                  data-testid={`merch-reorder-down-${p.printify_id}`}
                >
                  ↓
                </button>
              </div>
              {/* Pin to top shortcut */}
              {i !== 0 && (
                <button
                  type="button"
                  onClick={() => onMove(i, 0)}
                  className="text-[10px] uppercase tracking-wider text-gold-dark hover:text-ink font-bold flex-shrink-0 px-1"
                  data-testid={`merch-reorder-top-${p.printify_id}`}
                >
                  Pin top
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

